import { useRef, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardSection from '@shared/components/CardSection';
import { useConnectivityStore } from '@shared/hooks/useConnectivityStore';
import { useCreateOnDemand } from '../hooks/useCreateOnDemand';

export default function OnDemandRequestForm() {
  const isOffline = useConnectivityStore((s) => s.state) !== 'online';
  const createOnDemand = useCreateOnDemand();
  const [cellIdsText, setCellIdsText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleSubmit() {
    const cellIds = cellIdsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    createOnDemand.mutate({
      cellIds: cellIds.length > 0 ? cellIds : undefined,
      source: 'manual',
    });
  }

  return (
    <div data-testid="on-demand-request-form" className="flex flex-col gap-3">
      <CardSection title="On-Demand Update Request">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter cell IDs (comma or newline separated) or upload a request file.
        </Typography>

        <TextField
          label="Cell IDs"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={cellIdsText}
          onChange={(e) => setCellIdsText(e.target.value)}
          placeholder="GB400001, NO3B2020, ..."
          disabled={createOnDemand.isPending}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Or upload file:
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            disabled={isOffline || createOnDemand.isPending}
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isOffline || createOnDemand.isPending}
          startIcon={createOnDemand.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {createOnDemand.isPending ? 'Requesting…' : 'Request Update'}
        </Button>

        {createOnDemand.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createOnDemand.error?.message ?? 'Request failed'}
          </Alert>
        )}

        {createOnDemand.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Update request submitted: {createOnDemand.data.operationId}
          </Alert>
        )}
      </CardSection>
    </div>
  );
}
